import { CloseModalIcon } from "@/components/icons/close-modal-icon";
import { BaseModal } from "@/components/modal/base-modal";
import { FilledButton, TextButton } from "@/components/ui/buttons";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";
import { SwapPlanAdjustmentHero } from "./swap-plan-adjustment-hero";

interface SwapPlanAdjustmentModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  onUndoSwap: () => void;
  isConfirming?: boolean;
  isUndoing?: boolean;
  /**
   * When false, hides the "Undo Swap" button. Used for extra-meal swaps,
   * which the backend does not currently support undoing.
   */
  canUndo?: boolean;
  /** Label for the undo button. Defaults to "Undo Swap". */
  undoLabel?: string;
}

export const SwapPlanAdjustmentModal = memo(function SwapPlanAdjustmentModal({
  isOpen,
  onDismiss,
  onConfirm,
  onUndoSwap,
  isConfirming = false,
  isUndoing = false,
  canUndo = true,
  undoLabel = "Undo Swap",
}: SwapPlanAdjustmentModalProps) {
  const viewportHeight = useViewportHeight();
  const isBusy = isConfirming || isUndoing;

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss={!isBusy}
      initialBreakpoint={650 / viewportHeight}
      breakpoints={[0, 650 / viewportHeight]}
      scrollY={false}
      handle={false}
    >
      <div className="safe-area-bottom relative">
        <div className="flex w-full justify-end pt-7 pr-7">
          <TextButton
            type="button"
            onClick={onDismiss}
            disabled={isBusy}
            aria-label="Close"
            className="flex h-11 w-11 justify-center"
          >
            <CloseModalIcon className="text-[#663B9E]" />
          </TextButton>
        </div>

        <SwapPlanAdjustmentHero />

        <div className="mx-auto w-[300px] -translate-2.5 space-y-6 text-center">
          <h2 className={cn(textVariants.title1, "!font-semibold text-[#0F51BF]")}>
            Plan adjustment available
          </h2>
          <p className={cn(textVariants.bodySingleLine, "text-[#0F51BF]")}>
            Your recent change means a small adjustment is needed to stay on track.
          </p>
        </div>

        <div className="mt-11 flex w-full flex-1 flex-col justify-end space-y-4 px-8">
          {canUndo && (
            <TextButton
              color="black"
              textCase="normal"
              underline={true}
              onClick={onUndoSwap}
              disabled={isBusy}
              className="w-full !font-medium"
            >
              {isUndoing ? "Undoing..." : undoLabel}
            </TextButton>
          )}
          <FilledButton
            color="black"
            textCase="normal"
            onClick={onConfirm}
            disabled={isBusy}
            className="h-12 w-full !rounded-full !font-semibold"
          >
            {isConfirming ? "Saving..." : "Got It"}
          </FilledButton>
        </div>
      </div>
    </BaseModal>
  );
});
